import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';

export interface CauseItem {
  uuid: string;
  name: string;
  noOfOrganisation: number;
  noOfProjects: number;
  noOfPortfolios: number;
}

export interface CausesResponse {
  response: CauseItem[];
}

export interface CauseCheckNameResponse {
  /** true = name already taken; false = name is available */
  response: boolean;
}

export interface CauseCreateUpdatePayload {
  uuid: string | null;
  name: string;
}

export interface CauseCreateUpdateResponse {
  response: {
    uuid: string;
    submitted: boolean;
  };
}

export interface CauseDetailsResponse {
  response: CauseItem;
}

@Injectable({
  providedIn: 'root'
})
export class CauseService {
  private readonly base = '/v1/general/settings/causes';

  constructor(private apiService: RestfulApiService) {}

  /** Fetch all causes, optionally filtered server-side by search keyword. */
  getCauses(searchKeyword = ''): Observable<CausesResponse> {
    const params = searchKeyword ? { searchInput: searchKeyword } : {};
    return this.apiService.get(this.base, params) as Observable<CausesResponse>;
  }

  /** Returns true if the name is already taken.
   *  Pass uuid when updating an existing cause so the check excludes itself. */
  checkName(name: string, uuid?: string): Observable<CauseCheckNameResponse> {
    const payload = uuid ? { name, uuid } : { name };
    return this.apiService.post(`${this.base}/check-name`, payload) as Observable<CauseCheckNameResponse>;
  }

  /** Create or update a cause. Pass uuid: null to create. */
  createUpdate(payload: CauseCreateUpdatePayload): Observable<CauseCreateUpdateResponse> {
    return this.apiService.post(`${this.base}/create-update`, payload) as Observable<CauseCreateUpdateResponse>;
  }

  /** Fetch full cause details by UUID. */
  getDetails(uuid: string): Observable<CauseDetailsResponse> {
    return this.apiService.get(`${this.base}/details/${uuid}`) as Observable<CauseDetailsResponse>;
  }

  /** Delete a cause by UUID. */
  delete(uuid: string): Observable<unknown> {
    return this.apiService.delete(`${this.base}/delete/${uuid}`);
  }
}
